/**
 * FormDialog — schema-driven create/edit dialogs. Field values are typed by
 * `type`; empty optional fields are sent as null. Validation errors from Core
 * (zod fieldErrors) are surfaced by the caller's mutation toast.
 */
import { t as tx } from '@/i18n';
import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Input, Textarea } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/utils';

export type FieldType = 'text' | 'number' | 'money' | 'select' | 'textarea' | 'date' | 'datetime' | 'switch' | 'password' | 'email' | 'tel' | 'color' | 'multiselect';
export interface Field {
  name: string; label: string; type?: FieldType; required?: boolean; placeholder?: string; help?: string;
  options?: { value: string; label: string }[]; span?: 1 | 2; min?: number; max?: number; step?: number;
  show?: (v: Record<string, any>) => boolean; disabled?: boolean;
  /** A message when the (non-empty) value is not acceptable — shown under the field; the form is not sent until it is fixed. */
  validate?: (value: any, values: Record<string, any>) => string | undefined;
}

export function FormDialog({ open, onOpenChange, title, description, fields, initial, submitLabel = tx('common.save'), onSubmit, busy, children, wide, derive }: {
  open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: string; fields: Field[];
  initial?: Record<string, any>; submitLabel?: string; onSubmit: (values: Record<string, any>) => unknown | Promise<unknown>;
  busy?: boolean; children?: ReactNode; wide?: boolean;
  /** When a field changes, other fields can follow (e.g. a country fills in its currency and time zone). */
  derive?: (changed: string, values: Record<string, any>) => Record<string, any> | undefined;
}) {
  const [v, setV] = useState<Record<string, any>>({});
  useEffect(() => { if (open) setV(defaults(fields, initial)); }, [open]); // eslint-disable-line
  const set = (k: string, val: any) => setV((s) => { const n = { ...s, [k]: val }; return { ...n, ...(derive?.(k, n) ?? {}) }; });
  const missing = fields.filter((f) => f.required && (f.show?.(v) ?? true) && (v[f.name] === '' || v[f.name] === undefined || v[f.name] === null));
  const invalid: Record<string, string> = {};
  for (const f of fields) {
    const val = v[f.name];
    if (!f.validate || !(f.show?.(v) ?? true) || val === '' || val === undefined || val === null) continue;
    const msg = f.validate(val, v); if (msg) invalid[f.name] = msg;
  }
  const blocked = missing.length > 0 || Object.keys(invalid).length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (blocked) return;
    const out: Record<string, any> = {};
    for (const f of fields) {
      if (f.show && !f.show(v)) continue;
      const raw = v[f.name];
      if (f.type === 'number' || f.type === 'money') out[f.name] = raw === '' || raw === undefined ? null : Number(raw);
      else if (f.type === 'switch') out[f.name] = !!raw;
      else if (f.type === 'datetime') out[f.name] = raw ? new Date(raw).toISOString() : null;
      else out[f.name] = raw === '' ? null : raw;
    }
    await onSubmit(out);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto', wide ? 'sm:max-w-3xl' : 'sm:max-w-xl')}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {fields.filter((f) => f.show?.(v) ?? true).map((f) => (
              <label key={f.name} className={cn('block space-y-1', (f.span === 2 || f.type === 'textarea') && 'sm:col-span-2')}>
                <span className="text-xs font-semibold text-foreground/80">{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
                <FieldInput f={f} value={v[f.name]} onChange={(val) => set(f.name, val)} />
                {invalid[f.name] ? <span role="alert" className="block text-[11px] text-rose-600">{invalid[f.name]}</span>
                  : f.help && <span className="block text-[11px] text-muted-foreground">{f.help}</span>}
              </label>
            ))}
          </div>
          {children}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{tx('pg.shared_formdialog.cancel')}</Button>
            <Button type="submit" disabled={busy || blocked}>
              {busy && <Loader2 className="size-3.5 animate-spin" />} {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ f, value, onChange }: { f: Field; value: any; onChange: (v: any) => void }) {
  const common = { name: f.name, id: `f-${f.name}`, disabled: f.disabled, placeholder: f.placeholder, required: f.required };
  switch (f.type) {
    case 'select':
      return (
        <select name={f.name} id={`f-${f.name}`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={f.disabled}
          className="h-9 w-full rounded-md border bg-background px-2.5 text-sm outline-none focus:ring-2 focus:ring-ring">
          <option value="">{f.placeholder ?? tx('ui.select')}</option>
          {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case 'textarea': return <Textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...common} />;
    case 'multiselect': {
      const cur: string[] = Array.isArray(value) ? value : [];
      return (
        <div className="max-h-48 overflow-y-auto rounded-md border p-2">
          <div className="grid gap-1 sm:grid-cols-2">{(f.options ?? []).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-[13px]"><input type="checkbox" className="accent-primary" checked={cur.includes(o.value)}
              onChange={(e) => onChange(e.target.checked ? [...cur, o.value] : cur.filter((x) => x !== o.value))} />{o.label}</label>))}</div>
        </div>);
    }
    case 'switch':
      return (
        <button type="button" onClick={() => onChange(!value)} disabled={f.disabled}
          className={cn('relative mt-1 inline-flex h-5 w-9 items-center rounded-full transition-colors', value ? 'bg-primary' : 'bg-muted-foreground/30')}>
          <span className={cn('inline-block size-4 rounded-full bg-white shadow transition-transform', value ? 'translate-x-4 rtl:-translate-x-4' : 'translate-x-0.5 rtl:-translate-x-0.5')} />
        </button>
      );
    case 'color': return <input type="color" value={value ?? '#2563eb'} onChange={(e) => onChange(e.target.value)} className="h-9 w-16 cursor-pointer rounded-md border bg-background p-1" />;
    default: {
      const t = f.type === 'money' ? 'number' : f.type === 'datetime' ? 'datetime-local' : f.type ?? 'text';
      return <Input type={t} value={value ?? ''} min={f.min} max={f.max} step={f.step ?? (f.type === 'money' ? 0.01 : undefined)} onChange={(e) => onChange(e.target.value)} {...common} />;
    }
  }
}

function defaults(fields: Field[], initial?: Record<string, any>) {
  const out: Record<string, any> = {};
  for (const f of fields) {
    let v = initial?.[f.name];
    if (v === null || v === undefined) v = f.type === 'switch' ? false : '';
    if (f.type === 'date' && typeof v === 'string' && v.length > 10) v = v.slice(0, 10);
    if (f.type === 'datetime' && v) v = new Date(v).toISOString().slice(0, 16);
    out[f.name] = v;
  }
  return out;
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = tx('common.confirm'), destructive, reason, onConfirm, busy }: {
  open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: ReactNode; confirmLabel?: string;
  destructive?: boolean; reason?: string; onConfirm: (reason: string) => unknown; busy?: boolean;
}) {
  const [r, setR] = useState('');
  useEffect(() => { if (open) setR(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription asChild><div>{description}</div></DialogDescription>}
        </DialogHeader>
        {reason && <Textarea rows={2} placeholder={reason} value={r} onChange={(e) => setR(e.target.value)} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{tx('pg.shared_formdialog.cancel')}</Button>
          <Button variant={destructive ? 'destructive' : 'default'} disabled={busy || (!!reason && r.trim().length < 3)} onClick={() => onConfirm(r.trim())}>
            {busy && <Loader2 className="size-3.5 animate-spin" />} {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
